import { Query, selectPersistStateInit } from '@datorama/akita';
import { sessionAPIService } from 'core/services/sessionAPIService';
import { IUser } from 'modules/users/models/user.model';
import { usersQuery } from 'modules/users/state/users.query';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ISession } from '../models/session.model';
import { sessionStore } from './session.store';

export class SessionQuery extends Query<ISession> {
    readonly token$!: Observable<string | null>;
    readonly isLoggedIn$!: Observable<boolean>;
    readonly me$!: Observable<IUser | null>;

    constructor() {
        super(sessionStore);

        this.token$ = this.select('token');
        this.isLoggedIn$ = this.select((state) => !!state.token);
        this.me$ = this.select('me').pipe(
            switchMap((id) =>
                !id
                    ? of(null)
                    : usersQuery.selectEntity(id).pipe(map((x) => x ?? null))
            )
        );

        /**
         * This observable emits after Akita initialized the stores based on the storage's value.
         * We do NOT want to show persisted errors
         */
        selectPersistStateInit().subscribe(() => {
            this.store.setError(null);
        });

        this.isLoggedIn$.subscribe(async (value) => {
            if (!value) {
                return;
            }

            try {
                sessionStore.updateCurrent(await sessionAPIService.current());
            } catch (e: any) {
                // Prevent unhandled promise rejections from bubbling to the global handler (Sentry noise).
                // Most common case here is an expired/invalid token causing /me -> 401.
                console.warn('Failed to refresh current session user', e);
                sessionStore.reset();
            }
        });
    }

    get refreshToken(): string | null {
        const { refreshToken } = this.getValue();

        return refreshToken;
    }

    get token(): string | null {
        const { token } = this.getValue();

        return token;
    }

    get isLoggedIn(): boolean {
        return !!this.token;
    }

    get me(): IUser | null {
        const { me } = this.getValue();

        return !me ? null : (usersQuery.getEntity(me) ?? null);
    }
}

export const sessionQuery = new SessionQuery();
